import { Heart, MapPin, Clock, BadgeCheck, Flame, Sparkles, AlertCircle } from 'lucide-react';
import type { ListingCardProps, BadgeType } from '../types';
import { useNavigate } from 'react-router-dom';

function getBadgeStyle(badge: BadgeType): { className: string; icon: React.ReactNode } {
  const map: Record<BadgeType, { className: string; icon: React.ReactNode }> = {
    'Verified': { 
      className: 'bg-success/10 text-success border-success/20', 
      icon: <BadgeCheck className="w-3 h-3" /> 
    },
    'Hot Deal': { 
      className: 'bg-danger/10 text-danger border-danger/20', 
      icon: <Flame className="w-3 h-3" /> 
    },
    'New': { 
      className: 'bg-primary-100 text-primary-700 border-primary-200', 
      icon: <Sparkles className="w-3 h-3" /> 
    },
    'Urgent': { 
      className: 'bg-warning/10 text-warning-dark border-warning/20', 
      icon: <AlertCircle className="w-3 h-3" /> 
    },
  };
  return map[badge];
}

export default function ListingCard({
  listing,
  isSaved,
  onToggleSave,
  animationDelay,
}: ListingCardProps): JSX.Element {
  const navigate = useNavigate();
  const badgeStyle = getBadgeStyle(listing.badge);

  const handleCardClick = () => {
    navigate(`/item/${listing.uuid ?? listing.id}`);
  };

  const handleToggleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSave(listing.id, listing.uuid);
  };
  
  return (    <article
      onClick={handleCardClick}
      className="card-hover group h-full flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm border border-dark-100 hover:border-primary-200 hover:shadow-xl hover:shadow-primary-100/50 transition-all duration-300 cursor-pointer"
      style={{ animationDelay: `${animationDelay}s` }}
      aria-label={`${listing.title} — ${listing.price}`}
    >
      {/* Thumbnail with enhanced effects */}
      <div
        className="h-52 flex items-center justify-center relative overflow-hidden"
        style={{ background: listing.color }}
      >
        <img
          src={listing.img}
          alt={listing.title}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
          loading="lazy"
        />

        {/* Featured badge with gradient */}
        {listing.featured && (
          <div
            role="img"
            aria-label="Featured listing"
            className="absolute top-3 left-3 bg-gradient-to-r from-warning to-orange-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-lg flex items-center gap-1.5 shadow-lg backdrop-blur-md"
          >
            <Sparkles className="w-3 h-3" />
            FEATURED
          </div>
        )}

        {/* Save button with enhanced hover */}
        <button
          className="absolute top-3 right-3 w-9 h-9 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:shadow-lg hover:scale-110 active:scale-95 transition-all duration-200 group/heart z-10"
          onClick={handleToggleSave}
          aria-label={isSaved ? `Remove ${listing.title} from saved` : `Save ${listing.title}`}
          aria-pressed={isSaved}
        >
          <Heart 
            className={`w-4.5 h-4.5 transition-all duration-300 ${isSaved ? 'fill-danger text-danger scale-110' : 'text-dark-400 group-hover/heart:text-danger'}`} 
          />
        </button>
      </div>

      {/* Body with enhanced styling */}
      <div className="p-4 flex flex-col flex-1">
        {/* Tags row */}
        <div className="flex justify-between items-start gap-2 mb-2.5">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-md bg-dark-50 text-dark-500">
            {listing.tag}
          </span>
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-md border ${badgeStyle.className}`}>
            {badgeStyle.icon}
            {listing.badge}
          </span>
        </div>

        {/* Title with hover effect */}
        <h3 className="font-bold text-dark-900 mb-1 leading-snug line-clamp-2 min-h-[2.5rem] group-hover:text-primary-600 transition-colors duration-300 text-sm sm:text-base">
          {listing.title}
        </h3>

        {/* Price with gradient text */}
        <div className="flex items-center justify-between mb-3 mt-auto">
          <p
            aria-label={`Price: ${listing.price}`}
            className="text-lg font-extrabold text-primary-600"
          >
            {listing.price}
          </p>
        </div>

        {/* Meta info with icons */}
        <div className="flex items-center justify-between text-[11px] text-dark-400 pt-3 border-t border-dark-50">
          <span className="flex items-center gap-1 hover:text-dark-600 transition-colors max-w-[60%]">
            <MapPin className="w-3 h-3 text-dark-300" />
            <span className="truncate">{listing.location}</span>
          </span>
          <span className="flex items-center gap-1 hover:text-dark-600 transition-colors whitespace-nowrap">
            <Clock className="w-3 h-3 text-dark-300" />
            {listing.time}
          </span>
        </div>
      </div>
    </article>
  );
}
